import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { ActionCard } from './cards/action-feed-card';
import { AnimatePresence } from 'motion/react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ActionCardSkeleton } from './cards/action-card-skeleton';
import { cn } from '@shadcn/ui/lib/utils';
import { FeedHeader } from './feed-header';
import { CompletedActionsBadge } from './completed-actions-badge';
import { useActionCardDetails } from '../hooks/use-action-card-details';
import { motion } from 'motion/react';
import { debounce } from '../../../../helpers/debounce';
import { useSaltfish } from '../../../hooks/use-saltfish';
import { SimpleMessageFilters } from './simple-message-filters';
import { WeightKeys } from '@andsend/intelligence/shared';
import { useSimpleContactFilters } from '../hooks/use-simple-contact-filters';

interface ActionFeedProps {}

export const ActionFeedV2 = ({}: ActionFeedProps) => {
    const { activeFilter, contacts, isLoading, hasMoreData, isLoadingMore, loadMore, updateFilter } =
        useSimpleContactFilters({
            pageSize: 10,
        });

    const completedProspectIds: string[] = [];
    const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const feedContainerRef = useRef<HTMLDivElement>(null);
    const selectedProspectId = useParams().prospectId;
    const navigate = useNavigate();
    const { startSaltfishPlaylist } = useSaltfish();
    const [removedProspectIds, setRemovedProspectIds] = useState<string[]>([]);

    // TODO: Move inside of the card
    const { onClose: closeDetails, isOpen: isDetailsOpen } = useActionCardDetails();

    // Set ref for action cards
    const setCardRef = useCallback((prospectId: string, ref: HTMLDivElement | null) => {
        cardRefs.current[prospectId] = ref;
    }, []);

    const scrollToCard = useCallback((prospectId: string) => {
        const cardRef = cardRefs.current[prospectId];
        if (cardRef && feedContainerRef.current) {
            requestAnimationFrame(() => {
                const containerRect = feedContainerRef.current?.getBoundingClientRect();
                const cardRect = cardRef.getBoundingClientRect();

                if (!containerRect) return;

                const position = cardRect.top - containerRect.top + (feedContainerRef.current?.scrollTop || 0);

                if (position > 0 && feedContainerRef.current) {
                    feedContainerRef.current.scrollTo({
                        top: position - 45,
                        behavior: 'smooth',
                    });
                }
            });
        }
    }, []);

    // Handler for clicks on the container
    const handleContainerClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            // Only handle clicks if they're directly on the container itself
            if (e.target !== e.currentTarget) {
                return;
            }
            if (!selectedProspectId) return;

            // Check if click target is outside of the active card
            const clickedElement = e.target as HTMLElement;
            const activeCardRef = cardRefs.current[selectedProspectId];

            // click is not inside the active card
            if (!activeCardRef?.contains(clickedElement)) {
                // Check if clicked inside any other card ref
                const clickedInsideAnyCard = Object.values(cardRefs.current).some(
                    (ref) => ref && ref.contains(clickedElement)
                );

                // Only collapse if click was not inside any card
                if (!clickedInsideAnyCard) {
                    // Navigate to action feed without prospect ID to collapse
                    closeDetails();
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.delete('details');
                    setTimeout(
                        () => {
                            navigate(
                                { pathname: '/new/action-feed-v2', search: searchParams.toString() },
                                { preventScrollReset: true }
                            );
                        },
                        isDetailsOpen ? 300 : 0
                    );
                }
            }
        },
        [selectedProspectId, navigate, isDetailsOpen, closeDetails]
    );

    // Calculate completed prospects - memoized
    const completedProspects = useMemo(() => {
        return new Set(completedProspectIds);
    }, [completedProspectIds]);

    const debouncedScrollToCard = useCallback(
        debounce((prospectId: string) => {
            scrollToCard(prospectId);
        }, 300),
        [scrollToCard]
    );

    useEffect(() => {
        if (selectedProspectId && cardRefs.current[selectedProspectId]) {
            debouncedScrollToCard(selectedProspectId);
        }
    }, [selectedProspectId, debouncedScrollToCard]);

    // Handle infinite scroll
    useEffect(() => {
        const container = feedContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 400;

            if (isNearBottom && hasMoreData && !isLoadingMore) {
                loadMore();
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [hasMoreData, isLoadingMore, loadMore]);

    // Optimized contact filtering - simplified and faster
    const visibleContacts = useMemo(() => {
        if (removedProspectIds.length === 0) {
            // Fast path when no contacts are removed
            return contacts;
        }

        // Only filter when necessary
        const removedSet = new Set(removedProspectIds);
        return contacts.filter((contact) => !removedSet.has(contact.prospectId));
    }, [contacts, removedProspectIds]);

    // Optimized filter change handler
    const handleFilterChange = useCallback(
        (filter: WeightKeys | 'urgent') => {
            updateFilter(filter);
        },
        [updateFilter]
    );

    return (
        <div
            className={cn(
                'w-full h-full overflow-y-auto flex flex-col items-center bg-background-secondary overscroll-none'
            )}
            ref={feedContainerRef}
            onMouseDown={handleContainerClick}
        >
            <FeedHeader />
            <div className="sticky top-0 w-full flex flex-row justify-between z-100 bg-transparent px-4 pt-8 pointer-events-none overflow-visible">
                <div className="flex items-center gap-2 pointer-events-auto">
                    <CompletedActionsBadge variant="outline" className="h-8 bg-background-primary" />
                </div>
            </div>

            <div
                className={cn(
                    'relative flex flex-col gap-6 p-4 -top-12 items-center w-[calc(min(70%,1200px))] max-w-[calc(min(70%,1200px))] h-fit',
                    'overflow-x-visible'
                )}
            >
                <div className="pointer-events-auto flex flex-row gap-2 items-center">
                    <SimpleMessageFilters activeFilter={activeFilter} onFilterChange={handleFilterChange} />
                </div>

                {/* Main content area */}
                <div className="w-full flex flex-col gap-6">
                    {isLoading ? (
                        // Show skeletons while loading - faster animation
                        Array.from({ length: 5 }).map((_, index) => (
                            <motion.div
                                key={`skeleton-${activeFilter}-${index}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.15, delay: index * 0.05 }}
                            >
                                <ActionCardSkeleton />
                            </motion.div>
                        ))
                    ) : visibleContacts.length > 0 ? (
                        // Show contacts with optimized AnimatePresence
                        <AnimatePresence>
                            {visibleContacts.map((contact, index) => (
                                <motion.div
                                    key={contact.prospectId}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{
                                        duration: 0.2,
                                        delay: isLoading ? 0 : index * 0.05,
                                        ease: 'easeOut',
                                    }}
                                >
                                    <ActionCard
                                        prospectId={contact.prospectId}
                                        prospectData={{
                                            fullName:
                                                contact.fullName ||
                                                `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
                                            linkedinUserId: contact.linkedinUserId || '',
                                            linkedinProfileImgUrl: contact.linkedinProfileImgUrl || '',
                                            occupationTitle: contact.occupationTitle || '',
                                            organizationName: contact.organizationName || '',
                                            organizationId: contact.organizationId || '',
                                        }}
                                        ref={(ref) => setCardRef(contact.prospectId, ref)}
                                        isCompleted={
                                            completedProspects.has(contact.prospectId) &&
                                            !contacts.map((c) => c.prospectId).includes(contact.prospectId)
                                        }
                                        isExpanded={selectedProspectId === contact.prospectId}
                                        overrideMotivation={contact?.recommendedActions?.[0]?.reason}
                                        baseUrl="/new/action-feed-v2"
                                        onClick={(disableCollapse?: boolean) => {
                                            if (selectedProspectId !== contact.prospectId) {
                                                navigate({
                                                    pathname: `/new/action-feed-v2/${contact.prospectId}`,
                                                    search: location.search,
                                                });
                                            }

                                            if (!disableCollapse && selectedProspectId === contact.prospectId) {
                                                navigate({
                                                    pathname: '/new/action-feed-v2',
                                                    search: location.search,
                                                });
                                            }
                                        }}
                                        onRemoveCard={() =>
                                            setRemovedProspectIds((value) => [...value, contact.prospectId])
                                        }
                                    />
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    ) : (
                        // Empty state with faster animation
                        <motion.div
                            key={`empty-state-${activeFilter}`}
                            className="flex flex-col text-muted-foreground items-center justify-center h-full w-full py-20"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.3, ease: 'easeOut' }}
                        >
                            <p>
                                You are all set!{' '}
                                <Link to="/new/discover/suggestions" className="text-foreground-brand-primary">
                                    Go to discover
                                </Link>{' '}
                                to find new contacts.
                            </p>
                        </motion.div>
                    )}

                    {/* Infinite scroll loading indicator */}
                    {isLoadingMore && (
                        <motion.div
                            className="flex justify-center py-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                        >
                            <ActionCardSkeleton />
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
};
